import { useEffect, useRef, useState } from 'react';
import type { BoardSnapshot } from '../lib/api';
import { api } from '../lib/api';
import type { BoardDoc } from '../collab/BoardDoc';
import { Icon } from './icons';

function formatWhen(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return 'только что';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} мин назад`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} ч назад`;
  return new Date(ts).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  return `${(bytes / 1024).toFixed(1)} КБ`;
}

/**
 * Version history.
 *
 * A snapshot is a full copy of the board's Yjs state, taken on demand.
 * Restoring replays through the same collaboration socket everyone else is
 * on, so it needs no page reload and no special client handling — the doc
 * just receives an update, the same as if someone had made a large edit.
 */
export function HistoryPanel({ doc }: { doc: BoardDoc }) {
  const [snapshots, setSnapshots] = useState<BoardSnapshot[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  // Guards against a late response for a previous board overwriting this
  // one's list; a failed request shows an empty list instead of loading forever.
  const requestRef = useRef(0);
  const reload = () => {
    const id = ++requestRef.current;
    api.listSnapshots(doc.boardId).then(
      (list) => {
        if (id === requestRef.current) setSnapshots(list);
      },
      () => {
        if (id === requestRef.current) setSnapshots([]);
      },
    );
  };

  useEffect(reload, [doc.boardId]);

  const save = async () => {
    setBusy(true);
    try {
      await api.createSnapshot(doc.boardId);
      reload();
    } finally {
      setBusy(false);
    }
  };

  const restore = async (id: string) => {
    setBusy(true);
    try {
      await api.restoreSnapshot(doc.boardId, id);
      setConfirmId(null);
      reload();
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    setBusy(true);
    try {
      await api.deleteSnapshot(doc.boardId, id);
      reload();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="popover-stack history-panel">
      <button type="button" className="primary-btn" disabled={busy} onClick={save}>
        <Icon name="history" size={16} /> Сохранить текущую версию
      </button>

      <div className="history-list">
        {snapshots === null && <p className="popover-hint">Загрузка…</p>}
        {snapshots?.length === 0 && (
          <p className="popover-hint">Пока нет сохранённых версий. Первая создастся автоматически при восстановлении.</p>
        )}
        {snapshots?.map((s) => (
          <div key={s.id} className="history-row">
            <div className="history-row-main">
              <span className="history-row-label">{s.label}</span>
              <span className="history-row-meta">
                {formatWhen(s.createdAt)} · {formatSize(s.sizeBytes)}
              </span>
            </div>
            {confirmId === s.id ? (
              <div className="history-confirm">
                <span>Заменить доску этой версией?</span>
                <button type="button" className="primary-btn" disabled={busy} onClick={() => restore(s.id)}>
                  Да
                </button>
                <button type="button" className="menu-item" onClick={() => setConfirmId(null)}>
                  Отмена
                </button>
              </div>
            ) : (
              <div className="history-row-actions">
                <button
                  type="button"
                  className="layer-row-btn"
                  aria-label="Восстановить"
                  data-tip="Восстановить"
                  onClick={() => setConfirmId(s.id)}
                >
                  <Icon name="history" size={14} />
                </button>
                <button
                  type="button"
                  className="layer-row-btn"
                  aria-label="Удалить"
                  data-tip="Удалить"
                  onClick={() => remove(s.id)}
                >
                  <Icon name="trash" size={14} />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
